import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppNav from '../components/common/AppNav';
import { getPublicBoutiques, getPublicJourneys, type PublicBoutique, type Journey } from '../api/portalApi';
import { useCurrency } from '../contexts/CurrencyContext';
import '../styles/public.css';

const ACCENT = '#38bdf8';

function minJourneyPrice(j: Journey): { price: number; currency: string } | null {
  if (!j.durations?.length) return null;
  const sorted = [...j.durations].sort((a, b) => a.price - b.price);
  const first = sorted[0];
  if (!first) return null;
  return { price: first.price, currency: first.currency };
}

const HomePage: React.FC = () => {
  const { t } = useTranslation('landing');
  const { format: formatMoney } = useCurrency();
  const [boutiques, setBoutiques] = useState<PublicBoutique[]>([]);
  const [journeys, setJourneys]   = useState<Journey[]>([]);
  const [loadingB, setLoadingB]   = useState(true);
  const [loadingJ, setLoadingJ]   = useState(true);

  useEffect(() => {
    getPublicBoutiques()
      .then((r) => setBoutiques(r.boutiques))
      .catch(() => {})
      .finally(() => setLoadingB(false));
    getPublicJourneys()
      .then((r) => setJourneys(r.journeys.filter((j) => j.status === 'active')))
      .catch(() => {})
      .finally(() => setLoadingJ(false));
  }, []);

  return (
    <div className="home-pro">
      <AppNav accentColor={ACCENT} />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="home-hero">
        <div className="home-hero-bg" />
        <p className="home-hero-eyebrow">{t('homePage.eyebrow')}</p>
        <h1 className="home-hero-headline">
          {t('homePage.headline')}
        </h1>
        <p className="home-hero-sub">
          {t('homePage.sub')}
        </p>
        <div className="home-hero-ctas">
          <Link to="/shop" className="home-cta-btn home-cta-btn--primary">
            🛍️ {t('homePage.ctaBrowse')}
          </Link>
          <Link to="/journeys" className="home-cta-btn home-cta-btn--gold">
            ✈️ {t('homePage.ctaJourneys')}
          </Link>
          <Link to="/globe" className="home-cta-btn home-cta-btn--secondary">
            🌐 {t('homePage.ctaGlobe')}
          </Link>
        </div>
        <div className="home-stats-strip">
          <div className="home-stat-item">
            <div className="home-stat-num">30+</div>
            <div className="home-stat-label">{t('homePage.stat1Label')}</div>
          </div>
          <div className="home-stat-item">
            <div className="home-stat-num">5</div>
            <div className="home-stat-label">{t('homePage.stat2Label')}</div>
          </div>
          <div className="home-stat-item">
            <div className="home-stat-num">20–30%</div>
            <div className="home-stat-label">{t('homePage.stat3Label')}</div>
          </div>
        </div>
      </section>

      {/* ── Boutiques slider ─────────────────────────────── */}
      <section className="home-section">
        <p className="home-section-eyebrow" style={{ color: '#c8a06a' }}>{t('homePage.boutiqueEyebrow')}</p>
        <h2 className="home-section-title">{t('homePage.boutiqueTitle')}</h2>
        <p className="home-section-sub">
          {t('homePage.boutiqueSub')}
        </p>
        <div className="home-hscroll">
          {loadingB
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="home-card-skeleton home-card-skeleton--boutique" />
              ))
            : boutiques.map((b) => (
                <Link
                  key={b._id}
                  to={`/shop/boutique/${b._id}`}
                  className="home-boutique-card"
                >
                  <div className="home-boutique-cover">
                    {b.coverImageUrl ? (
                      <img src={b.coverImageUrl} alt={b.name} loading="lazy" />
                    ) : (
                      <div className="home-boutique-cover-empty">🌿</div>
                    )}
                    {b.logoUrl && (
                      <img src={b.logoUrl} alt="" className="home-boutique-logo" />
                    )}
                  </div>
                  <div className="home-boutique-body">
                    <p className="home-boutique-location">{b.location}</p>
                    <p className="home-boutique-name">{b.name}</p>
                    <p className="home-boutique-tagline">{b.tagline || b.bio}</p>
                  </div>
                </Link>
              ))}
        </div>
        {!loadingB && boutiques.length > 0 && (
          <Link to="/shop" className="home-section-link" style={{ color: '#c8a06a' }}>
            {t('homePage.seeAllBoutiques')}
          </Link>
        )}
      </section>

      {/* ── Journeys slider ──────────────────────────────── */}
      <section className="home-section" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 80 }}>
        <p className="home-section-eyebrow" style={{ color: '#f59e0b' }}>{t('homePage.journeyEyebrow')}</p>
        <h2 className="home-section-title">{t('homePage.journeyTitle')}</h2>
        <p className="home-section-sub">
          {t('homePage.journeySub')}
        </p>
        <div className="home-hscroll">
          {loadingJ
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="home-card-skeleton home-card-skeleton--journey" />
              ))
            : journeys.map((j) => {
                const minP = minJourneyPrice(j);
                return (
                  <Link
                    key={j._id}
                    to={`/journeys/${j._id}`}
                    className="home-journey-card"
                  >
                    <div className="home-journey-cover">
                      {j.coverImageUrl ? (
                        <img src={j.coverImageUrl} alt={j.title} loading="lazy" />
                      ) : (
                        <div className="home-boutique-cover-empty">✈️</div>
                      )}
                      {j.spotsRemaining > 0 && (
                        <div className="home-journey-spots">
                          {j.spotsRemaining} spot{j.spotsRemaining !== 1 ? 's' : ''} left
                        </div>
                      )}
                    </div>
                    <div className="home-journey-body">
                      <p className="home-journey-location">{j.location}</p>
                      <p className="home-journey-title">{j.title}</p>
                      <p className="home-journey-tagline">{j.tagline}</p>
                      {minP && (
                        <div className="home-journey-footer">
                          <span className="home-journey-price">
                            {t('homePage.from')} {formatMoney(minP.price, minP.currency)}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
        </div>
        {!loadingJ && journeys.length > 0 && (
          <Link to="/journeys" className="home-section-link" style={{ color: '#f59e0b' }}>
            {t('homePage.seeAllJourneys')}
          </Link>
        )}
      </section>

      {/* ── Industrial Tools (China) ─────────────────────── */}
      <section className="home-china-section">
        <div className="home-china-inner">
          <div>
            <p className="home-section-eyebrow" style={{ color: '#ef4444' }}>{t('homePage.chinaEyebrow')}</p>
            <h2 className="home-section-title">{t('homePage.chinaTitle')}</h2>
            <p className="home-section-sub">
              {t('homePage.chinaSub')}
            </p>
            <div className="home-china-steps">
              <div className="home-china-step">
                <div className="home-china-step-num">1</div>
                <div className="home-china-step-text">
                  <strong>{t('homePage.chinaStep1Title')}</strong>
                  {' '}{t('homePage.chinaStep1Body')}
                </div>
              </div>
              <div className="home-china-step">
                <div className="home-china-step-num">2</div>
                <div className="home-china-step-text">
                  <strong>{t('homePage.chinaStep2Title')}</strong>
                  {' '}{t('homePage.chinaStep2Body')}
                </div>
              </div>
              <div className="home-china-step">
                <div className="home-china-step-num">3</div>
                <div className="home-china-step-text">
                  <strong>{t('homePage.chinaStep3Title')}</strong>
                  {' '}{t('homePage.chinaStep3Body')}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 28 }}>
              <Link to="/china" className="home-cta-btn home-cta-btn--secondary" style={{ display: 'inline-flex', textDecoration: 'none', border: '1px solid rgba(239,68,68,0.35)', color: '#ef4444' }}>
                ⚙️ {t('homePage.chinaCta')}
              </Link>
            </div>
          </div>
          <div className="home-china-visual">
            <div className="home-china-route">
              <p className="home-china-route-label">{t('homePage.chinaSourcedFrom')}</p>
              <p className="home-china-route-city">🇨🇳 China</p>
              <div className="home-china-route-arrow">↓</div>
              <p className="home-china-route-city">🌍 {t('homePage.chinaDeliveredTo')}</p>
            </div>
            <div className="home-china-tags">
              {['Sewing machines', 'Grain mills', 'Welding sets', 'Agri equipment', 'Food processors'].map((t_) => (
                <span key={t_} className="home-china-tag">{t_}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Invest ───────────────────────────────────────── */}
      <section className="home-invest-section">
        <div className="home-invest-inner">
          <div>
            <p className="home-section-eyebrow" style={{ color: '#22c55e' }}>{t('homePage.investEyebrow')}</p>
            <h2 className="home-section-title">{t('homePage.investTitle')}</h2>
            <p className="home-section-sub">
              {t('homePage.investSub')}
            </p>
            <div className="home-invest-cards">
              <div className="home-invest-card">
                <div className="home-invest-card-icon">📦</div>
                <div>
                  <p className="home-invest-card-title">{t('homePage.investCard1Title')}</p>
                  <p className="home-invest-card-desc">
                    {t('homePage.investCard1Desc')}
                  </p>
                </div>
              </div>
              <div className="home-invest-card">
                <div className="home-invest-card-icon">📊</div>
                <div>
                  <p className="home-invest-card-title">{t('homePage.investCard2Title')}</p>
                  <p className="home-invest-card-desc">
                    {t('homePage.investCard2Desc')}
                  </p>
                </div>
              </div>
              <div className="home-invest-card">
                <div className="home-invest-card-icon">🔒</div>
                <div>
                  <p className="home-invest-card-title">{t('homePage.investCard3Title')}</p>
                  <p className="home-invest-card-desc">
                    {t('homePage.investCard3Desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="home-invest-roi">
            <div className="home-invest-roi-pct">20–30%</div>
            <div className="home-invest-roi-label">{t('homePage.investRoiLabel')}</div>
            <p className="home-invest-roi-note">
              {t('homePage.investRoiNote')}
            </p>
            <Link to="/login" className="home-cta-btn home-cta-btn--primary" style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center' }}>
              {t('homePage.applyToInvest')}
            </Link>
            <p style={{ fontSize: '0.7rem', color: 'rgba(241,245,249,0.35)', marginTop: 14, lineHeight: 1.5 }}>
              {t('homePage.investCuratedNote')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Footer CTA ───────────────────────────────────── */}
      <section className="home-footer-cta">
        <h2 className="home-footer-cta-title">{t('homePage.footerCtaTitle')}</h2>
        <p className="home-footer-cta-sub">
          {t('homePage.footerCtaSub')}
        </p>
        <div className="home-hero-ctas">
          <Link to="/africa" className="home-cta-btn home-cta-btn--primary" style={{ textDecoration: 'none', background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
            🌿 {t('homePage.footerExploreAfrica')}
          </Link>
          <Link to="/china" className="home-cta-btn home-cta-btn--gold" style={{ textDecoration: 'none', background: 'linear-gradient(135deg,#ef4444,#b91c1c)', color: '#fff' }}>
            🏮 {t('homePage.footerChina')}
          </Link>
          <Link to="/contact-us" className="home-cta-btn home-cta-btn--secondary" style={{ textDecoration: 'none' }}>
            💬 {t('homePage.footerContact')}
          </Link>
        </div>
        <div className="home-footer-links">
          <Link to="/privacy-policy" className="home-footer-link">{t('homePage.footerPrivacy')}</Link>
          <Link to="/terms-of-service" className="home-footer-link">{t('homePage.footerTerms')}</Link>
          <Link to="/globe" className="home-footer-link">{t('homePage.footerGlobe')}</Link>
          <Link to="/login" className="home-footer-link">{t('homePage.footerSignIn')}</Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
